import { useState, useEffect, useRef } from 'react';

export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const isVisible = scrollY > 400;
        const pct = docHeight > 0 ? Math.min((scrollY / docHeight) * 100, 100) : 0;

        setVisible(isVisible);
        setScrollPct(pct);
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // SVG circle progress ring (desktop)
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollPct / 100) * circumference;

  return (
    <>
      {/* ── Mobile: slim progress bar at top + floating tap-to-top pill ── */}
      <div className="lg:hidden" aria-hidden="true">
        {/* Fixed progress bar — always visible once scrolled */}
        <div
          className="fixed top-0 left-0 right-0 z-[70] h-[3px] pointer-events-none"
          style={{ background: 'rgba(26,37,64,0.08)' }}
        >
          <div
            className="h-full transition-all duration-150 ease-out"
            style={{
              width: `${scrollPct}%`,
              background: 'linear-gradient(90deg, #E8B84B 0%, #D4A43D 100%)',
            }}
          />
        </div>

        {/* Floating back-to-top pill (mobile) */}
        <button
          onClick={handleClick}
          aria-label="Back to top"
          className={`
            fixed bottom-28 right-4 z-[60]
            flex items-center gap-1
            px-2.5 py-1.5 rounded-full
            text-xs font-semibold whitespace-nowrap cursor-pointer
            transition-all duration-300 ease-in-out
            ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'}
          `}
          style={{
            background: '#1A2540',
            color: '#E8B84B',
            boxShadow: '0 2px 10px rgba(26,37,64,0.25)',
            border: '1px solid rgba(232,184,75,0.22)',
          }}
        >
          <svg
            width="12" height="12"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
            className="shrink-0"
          >
            <path d="M7 11V3M7 3L3 7M7 3l4 4" stroke="#E8B84B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Top</span>
        </button>
      </div>

      {/* ── Desktop: circular progress ring button ── */}
      <button
        onClick={handleClick}
        aria-label="Back to top"
        className={`
          hidden lg:flex
          fixed bottom-8 right-8 z-[60]
          w-9 h-9 items-center justify-center
          transition-all duration-300 ease-in-out cursor-pointer
          ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}
        style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.20))' }}
      >
        {/* Progress ring */}
        <svg
          width="36"
          height="36"
          viewBox="0 0 48 48"
          className="absolute inset-0"
          aria-hidden="true"
        >
          {/* Track */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            fill="#1A2540"
            stroke="rgba(232,184,75,0.18)"
            strokeWidth="2.5"
          />
          {/* Progress arc */}
          <circle
            cx="24"
            cy="24"
            r={radius}
            fill="none"
            stroke="#E8B84B"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 24 24)"
            style={{ transition: 'stroke-dashoffset 0.2s ease' }}
          />
        </svg>

        {/* Arrow icon */}
        <svg
          width="14" height="14"
          viewBox="0 0 18 18"
          fill="none"
          aria-hidden="true"
          className="relative z-10"
        >
          <path d="M9 14V4M9 4L4 9M9 4l5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </>
  );
}
